import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { BASE_URL } from "@/config/api.config";

const CreatePost = () => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    contentType: "paragraph",
    paragraphContent: "",
    videoLink: "",
    tags: [],
    category: "",
    status: "draft",
  });

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState({});

  const contentTypes = [
    { value: "paragraph", label: "Text Paragraph" },
    { value: "image", label: "Image" },
    { value: "video", label: "Video File" },
    { value: "videoLink", label: "Video Link" },
    { value: "other", label: "Other" },
  ];

  const statusOptions = [
    { value: "draft", label: "Draft" },
    { value: "published", label: "Published" },
    { value: "archived", label: "Archived" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);

    // Clear file error when user selects a file
    if (errors.file) {
      setErrors((prev) => ({ ...prev, file: "" }));
    }

    if (selectedFile.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setFilePreview(reader.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const handleTagInputChange = (e) => setTagInput(e.target.value);

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput("");
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required fields validation
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.description.trim())
      newErrors.description = "Description is required";
    if (!formData.category.trim()) newErrors.category = "Category is required";

    // Content type specific validation
    if (
      formData.contentType === "paragraph" &&
      !formData.paragraphContent.trim()
    ) {
      newErrors.paragraphContent = "Content is required for paragraph type";
    }

    if (formData.contentType === "videoLink" && !formData.videoLink.trim()) {
      newErrors.videoLink = "Video link is required";
    }

    if (["image", "video"].includes(formData.contentType) && !file) {
      newErrors.file = `File is required for ${formData.contentType} type`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      submitData.append("contentType", formData.contentType);
      submitData.append("status", formData.status);
      submitData.append("category", formData.category);
      submitData.append("tags", JSON.stringify(formData.tags));

      if (formData.contentType === "paragraph") {
        submitData.append("paragraphContent", formData.paragraphContent);
      }
      if (formData.contentType === "videoLink") {
        submitData.append("videoLink", formData.videoLink);
        if (formData.paragraphContent) {
          submitData.append("paragraphContent", formData.paragraphContent);
        }
      }
      if (["image", "video"].includes(formData.contentType)) {
        if (file) {
          submitData.append("file", file);
        } else {
          toast.error(`File is required for ${formData.contentType}`);
          setIsSubmitting(false);
          return;
        }
      }
      if (formData.contentType === "other") {
        if (formData.paragraphContent) {
          submitData.append("paragraphContent", formData.paragraphContent);
        }
        if (file) {
          submitData.append("file", file);
        }
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        toast.error("Authentication token not found. Please log in.");
        setIsSubmitting(false);
        return;
      }

      const response = await axios.post(
        `${BASE_URL}/api/v1/admin/upload`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        toast.success("Content uploaded successfully!");
        setFormData({
          title: "",
          description: "",
          contentType: "paragraph",
          paragraphContent: "",
          videoLink: "",
          tags: [],
          category: "",
          status: "draft",
        });
        setFile(null);
        setFilePreview(null);
        setErrors({});
      } else {
        toast.error(response.data.message || "Upload failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Upload error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to render required field asterisk
  const RequiredMark = () => <span className="text-red-500 ml-1">*</span>;

  return (
    <div className="min-h-screen py-10 px-4 bg-gray-50">
      <div className="max-w-3xl mx-auto bg-white p-8 shadow-xl rounded-2xl border border-gray-200">
        <h2 className="text-3xl font-semibold mb-8 text-center text-gray-800">
          Create New Post
        </h2>

        <div className="mb-4 text-sm text-gray-500">
          Fields marked with <span className="text-red-500">*</span> are
          required
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Inputs: Content Type, Title, Description, Category, Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Content Type
                <RequiredMark />
              </label>
              <select
                name="contentType"
                value={formData.contentType}
                onChange={handleChange}
                className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                  errors.contentType ? "border-red-500" : ""
                }`}
                required
              >
                {contentTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
              {errors.contentType && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.contentType}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title
                <RequiredMark />
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter title here"
                className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                  errors.title ? "border-red-500" : ""
                }`}
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
              <RequiredMark />
            </label>
            <textarea
              name="description"
              value={formData.description}
              placeholder="Enter description here"
              onChange={handleChange}
              className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                errors.description ? "border-red-500" : ""
              }`}
              rows={3}
              required
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Category
                <RequiredMark />
              </label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleChange}
                placeholder="Enter category here"
                className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                  errors.category ? "border-red-500" : ""
                }`}
                required
              />
              {errors.category && (
                <p className="mt-1 text-sm text-red-500">{errors.category}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
                <RequiredMark />
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                  errors.status ? "border-red-500" : ""
                }`}
                required
              >
                {statusOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {errors.status && (
                <p className="mt-1 text-sm text-red-500">{errors.status}</p>
              )}
            </div>
          </div>

          {/* Tag Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tags
            </label>
            <div className="flex">
              <input
                type="text"
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagKeyDown}
                placeholder="Press Enter to add tag"
                className="flex-grow px-3 py-2 border rounded-l-lg focus:ring-2 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={addTag}
                className="bg-blue-500 text-white px-4 py-2 rounded-r-lg hover:bg-blue-600"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.tags.map((tag, i) => (
                <span
                  key={i}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full flex items-center"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Conditional Inputs */}
          {formData.contentType === "paragraph" && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Content
                <RequiredMark />
              </label>
              <textarea
                name="paragraphContent"
                value={formData.paragraphContent}
                onChange={handleChange}
                placeholder="Enter your content..."
                className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                  errors.paragraphContent ? "border-red-500" : ""
                }`}
                rows={6}
                required
              />
              {errors.paragraphContent && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.paragraphContent}
                </p>
              )}
            </div>
          )}

          {formData.contentType === "videoLink" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Video Link
                  <RequiredMark />
                </label>
                <input
                  type="url"
                  name="videoLink"
                  value={formData.videoLink}
                  onChange={handleChange}
                  className={`w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400 ${
                    errors.videoLink ? "border-red-500" : ""
                  }`}
                  placeholder="YouTube/Vimeo link"
                  required
                />
                {errors.videoLink && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.videoLink}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Description (optional)
                </label>
                <textarea
                  name="paragraphContent"
                  value={formData.paragraphContent}
                  onChange={handleChange}
                  className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-400"
                  rows={4}
                />
              </div>
            </>
          )}

          {["image", "video", "other"].includes(formData.contentType) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Upload File
                {["image", "video"].includes(formData.contentType) && (
                  <RequiredMark />
                )}
              </label>
              <input
                type="file"
                onChange={handleFileChange}
                className={`w-full mt-1 px-3 py-2 border rounded-lg ${
                  errors.file ? "border-red-500" : ""
                }`}
                accept={
                  formData.contentType === "image"
                    ? "image/*"
                    : formData.contentType === "video"
                    ? "video/*"
                    : "*/*"
                }
                required={["image", "video"].includes(formData.contentType)}
              />
              {errors.file && (
                <p className="mt-1 text-sm text-red-500">{errors.file}</p>
              )}
              {filePreview && formData.contentType === "image" && (
                <div className="mt-3">
                  <img
                    src={filePreview}
                    alt="Preview"
                    className="rounded-lg max-h-60"
                  />
                </div>
              )}
              {file && (
                <div className="mt-2 text-gray-600 text-sm">
                  Selected file: {file.name} ({(file.size / 1024).toFixed(2)}{" "}
                  KB)
                </div>
              )}
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 px-4 text-white font-semibold rounded-lg transition duration-300 ${
                isSubmitting
                  ? "bg-blue-300 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Uploading..." : "Upload Content"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePost;
